import { httpsCallable } from 'firebase/functions'
import { ensurePmmAppCheckReady, getPmmFirebaseServices } from '../lib/firebase/client'
import type {
  PartnerConnectionView,
  PartnerConsentDecision,
  PartnerConsentProjection,
  PartnerConsentScope,
  PartnerCurrentSupport,
  PartnerDataExport,
  PartnerInvitePreview,
  PartnerInviteResult,
  PartnerPreference,
  PartnerResponse,
  PartnerWorkspaceView,
  SavePartnerCurrentSupportRequest,
  SavePartnerPreferenceRequest,
  SavePartnerResponseSetRequest,
} from '../domain/partner'

export class PartnerRepository {
  async getConnection(): Promise<PartnerConnectionView> {
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<Record<string, never>, PartnerConnectionView>(functions, 'getPmmPartnerConnection')
    return (await call({})).data
  }

  async createInvite(email: string, whatsappProvided: boolean, collectionNoticeAccepted: boolean, clientMutationId: string): Promise<PartnerInviteResult> {
    const { functions } = await getPmmFirebaseServices()
    await ensurePmmAppCheckReady()
    const call = httpsCallable<{ email: string; whatsappProvided: boolean; collectionNoticeAccepted: boolean; clientMutationId: string }, PartnerInviteResult>(functions, 'createPmmPartnerInvite')
    return (await call({ email, whatsappProvided, collectionNoticeAccepted, clientMutationId })).data
  }

  async getInvitePreview(token: string): Promise<PartnerInvitePreview> {
    const { functions } = await getPmmFirebaseServices()
    await ensurePmmAppCheckReady()
    const call = httpsCallable<{ token: string }, PartnerInvitePreview>(functions, 'getPmmPartnerInvitePreview')
    return (await call({ token })).data
  }

  async declineInvite(token: string): Promise<void> {
    const { functions } = await getPmmFirebaseServices()
    await ensurePmmAppCheckReady()
    const call = httpsCallable<{ token: string }, unknown>(functions, 'declinePmmPartnerInvite')
    await call({ token })
  }

  async claimInvite(token: string): Promise<void> {
    const { auth, functions } = await getPmmFirebaseServices()
    await auth.currentUser?.getIdToken(true)
    const call = httpsCallable<{ token: string }, { refreshRequired: boolean }>(functions, 'claimPmmPartnerInvite')
    await call({ token })
    await auth.currentUser?.getIdToken(true)
  }

  async getWorkspace(): Promise<PartnerWorkspaceView> {
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<Record<string, never>, PartnerWorkspaceView>(functions, 'getPmmPartnerWorkspace')
    return (await call({})).data
  }

  async recordConsent(scope: PartnerConsentScope, decision: PartnerConsentDecision, previousEventId: string | null): Promise<PartnerConsentProjection> {
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<{ scope: PartnerConsentScope; decision: PartnerConsentDecision; previousEventId: string | null; uiSource: string }, { consent: PartnerConsentProjection }>(functions, 'recordPmmPartnerConsent')
    return (await call({ scope, decision, previousEventId, uiSource: 'partner_privacy_centre' })).data.consent
  }

  async savePreference(request: SavePartnerPreferenceRequest): Promise<PartnerPreference> {
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<SavePartnerPreferenceRequest, { preference: PartnerPreference }>(functions, 'savePmmPartnerPreference')
    return (await call(request)).data.preference
  }

  async deletePreference(preferenceId: string): Promise<void> {
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<{ preferenceId: string }, unknown>(functions, 'deletePmmPartnerPreference')
    await call({ preferenceId })
  }

  async saveResponseSet(request: SavePartnerResponseSetRequest): Promise<PartnerResponse[]> {
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<SavePartnerResponseSetRequest, { responses: PartnerResponse[] }>(functions, 'savePmmPartnerResponseSet')
    return (await call(request)).data.responses
  }

  async saveCurrentSupport(request: SavePartnerCurrentSupportRequest): Promise<PartnerCurrentSupport> {
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<SavePartnerCurrentSupportRequest, { currentSupport: PartnerCurrentSupport }>(functions, 'savePmmPartnerCurrentSupport')
    return (await call(request)).data.currentSupport
  }

  async exportData(): Promise<PartnerDataExport> {
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<Record<string, never>, { export: PartnerDataExport }>(functions, 'exportPmmPartnerData')
    return (await call({})).data.export
  }

  async unlink(): Promise<void> {
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<Record<string, never>, unknown>(functions, 'unlinkPmmPartnerRelationship')
    await call({})
  }

  async deleteData(): Promise<void> {
    const { functions } = await getPmmFirebaseServices()
    const call = httpsCallable<Record<string, never>, unknown>(functions, 'deletePmmPartnerData')
    await call({})
  }
}
